import { useCallback, useMemo, useState, type ReactNode } from 'react'
import { doc } from 'firebase/firestore'
import type { DocumentReference } from 'firebase/firestore'
import { useLocaleContext } from 'fbtee'
import { db } from '../lib/firebase'
import { setDoc } from '../lib/firestore'
import { useFirestoreDoc } from '../hooks/useFirestore'
import { useAuth } from '../hooks/useAuth'
import { useDeviceClock } from '../hooks/useDeviceClock'
import { log } from '../lib/logger'
import { track } from '../lib/analytics'
import {
  formatClockRange as formatClockRangeFn,
  formatClockTime,
  resolveTimeFormat,
} from '../lib/timeFormat'
import type { TimeFormatPref, UserPreferences } from '@shared/preferences'
import { TimeFormatContext } from './TimeFormatContext'

const STORAGE_KEY = 'beacon_time_format'

function isPref(value: unknown): value is TimeFormatPref {
  return value === 'auto' || value === '12h' || value === '24h'
}

function readLocal(): TimeFormatPref {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return isPref(raw) ? raw : 'auto'
  } catch {
    return 'auto'
  }
}

function writeLocal(pref: TimeFormatPref) {
  try {
    localStorage.setItem(STORAGE_KEY, pref)
  } catch {
    // Not remembered past this visit; the choice still applies now.
  }
}

/**
 * The clock format for set times, app-wide — one provider rather than a hook
 * per component, since every grid block reads it. Until the user picks one it
 * follows the phone's 12/24-hour setting (native app), else the app language. localStorage first so it applies before Firestore
 * answers; signed-in users also sync it to `private/preferences`.
 */
export function TimeFormatProvider({ children }: { children: ReactNode }) {
  const { locale } = useLocaleContext()
  const { signedInUid } = useAuth()
  const [pref, setPrefState] = useState<TimeFormatPref>(readLocal)
  const deviceClock = useDeviceClock()

  const ref = useMemo(
    () =>
      signedInUid
        ? (doc(db, 'users', signedInUid, 'private', 'preferences') as DocumentReference<UserPreferences>)
        : null,
    [signedInUid],
  )
  const { data: remote } = useFirestoreDoc<UserPreferences>(ref)

  // A remote value always wins (`auto` included, once picked back in Settings).
  // Adopted during render, and only when it changes, so a resubscribe
  // delivering the same value can't undo a newer local choice.
  const remoteTimeFormat = remote?.timeFormat
  const remotePref = isPref(remoteTimeFormat) ? remoteTimeFormat : null
  const [trackedRemote, setTrackedRemote] = useState<TimeFormatPref | null>(null)
  if (trackedRemote !== remotePref) {
    setTrackedRemote(remotePref)
    if (remotePref && remotePref !== pref) {
      setPrefState(remotePref)
      writeLocal(remotePref)
    }
  }

  const setPref = useCallback(
    (next: TimeFormatPref) => {
      setPrefState(next)
      writeLocal(next)
      if (next !== 'auto') track('time_format_set', { time_format: next })
      if (ref) {
        void setDoc(ref, { timeFormat: next }, { merge: true }).catch(() => {
          log('time-format', 'failed to sync time format preference')
        })
      }
    },
    [ref],
  )

  const hourCycle = resolveTimeFormat(pref, locale, deviceClock)
  const autoHourCycle = resolveTimeFormat('auto', locale, deviceClock)
  const formatClock = useCallback((hhmm: string) => formatClockTime(hhmm, hourCycle, locale), [hourCycle, locale])
  const formatClockRange = useCallback(
    (start: string, end: string) => formatClockRangeFn(start, end, hourCycle, locale),
    [hourCycle, locale],
  )
  const value = useMemo(
    () => ({ pref, hourCycle, autoHourCycle, locale, setPref, formatClock, formatClockRange }),
    [pref, hourCycle, autoHourCycle, locale, setPref, formatClock, formatClockRange],
  )

  return <TimeFormatContext.Provider value={value}>{children}</TimeFormatContext.Provider>
}
